import {
    Button,
    Input,
    InputOnChangeData,
    mergeClasses,
    SelectTabData,
    SelectTabEvent,
    Tab,
    TabList,
    TabValue,
    Toolbar,
    ToolbarGroup,
} from '@fluentui/react-components';
import { Dismiss24Regular, Search24Regular } from '@fluentui/react-icons';
import { app } from '@microsoft/teams-js';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { useToolbarStyles } from '../../controls';
import {
    selectAppConfigurationSearchQuery,
    setSearchQuery,
} from '../../store/features/appConfiguration/appConfigurationSlice';
import { useAppDispatch, useSelector } from '../../store/hooks';
import { useAppConfigurationPageStyles } from './AppConfigurationPage.styles';
import AutoRepliesPanel from './Panels/AutoRepliesPanel/AutoRepliesPanel';
import CategoriesConfigPanel from './Panels/CategoriesConfigPanel/CategoriesConfigPanel';
import FlightNumbersPanel from './Panels/FlightNumbersPanel/FlightNumbersPanel';
import UnableToNotifyReasonsPanel from './Panels/UnableToNotifyReasonsPanel/UnableToNotifyReasonsPanel';
import WelcomeMessagesPanel from './Panels/WelcomeMessagesPanel/WelcomeMessagesPanel';
import AllowedCrewMembersPanel from './Panels/AllowedCrewMembersPanel/AllowedCrewMembersPanel';
import ConversationStatusesPanel from './Panels/ConversationStatusesPanel/ConversationStatusesPanel';

enum AppConfigTabs {
    WelcomeMessages,
    CategoriesConfig,
    AutoReplies,
    FlightNumbers,
    UnableToNotifyReasons,
    AllowedCrewMembers,
    ConversationStatuses,
}

const AppConfigurationPage = () => {
    const { t } = useTranslation();
    const styles = useAppConfigurationPageStyles();
    const toolbarStyles = useToolbarStyles();
    const dispatch = useAppDispatch();
    const searchQuery = useSelector(selectAppConfigurationSearchQuery);
    const [search, setSearch] = useState<string>(searchQuery);

    const [selectedValue, setSelectedValue] = React.useState<TabValue>(
        AppConfigTabs.WelcomeMessages
    );

    const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
        setSelectedValue(data.value);
    };

    useEffect(() => {
        app.notifySuccess();
    }, []);

    const searchDebounced = useDebouncedCallback(
        useCallback(
            (
                _: React.ChangeEvent<HTMLInputElement>,
                data: InputOnChangeData
            ) => {
                dispatch(setSearchQuery(data.value ?? ''));
            },
            [dispatch]
        ),
        500
    );

    const onQueryChange = useCallback(
        (_: any, data: InputOnChangeData) => {
            setSearch(data.value);
            searchDebounced(_, data);
        },
        [searchDebounced]
    );

    const onClearSearchTextClick = useCallback(() => {
        setSearch('');
        dispatch(setSearchQuery(''));
    }, [dispatch]);

    return (
        <div className={styles.root}>
            <Toolbar
                aria-label="Flights"
                className={mergeClasses(
                    toolbarStyles.toolbar,
                    styles.configurationToolbar
                )}
            >
                <ToolbarGroup
                    role="presentation"
                    className={mergeClasses(
                        toolbarStyles.toolbarGroup,
                        styles.tabGroup
                    )}
                >
                    <TabList
                        selectedValue={selectedValue}
                        onTabSelect={onTabSelect}
                        className={styles.tabList}
                    >
                        <Tab
                            value={AppConfigTabs.WelcomeMessages}
                            className={styles.tab}
                        >
                            {t('AppConfiguration_WelcomeMessages_Section')}
                        </Tab>
                        <Tab value={AppConfigTabs.ConversationStatuses} className={styles.tab}>
                            {t('AppConfiguration_ConversationStatuses_Section')}
                        </Tab>
                        <Tab
                            value={AppConfigTabs.CategoriesConfig}
                            className={styles.tab}
                        >
                            {t('AppConfiguration_CategoriesConfig_Section')}
                        </Tab>
                        <Tab
                            value={AppConfigTabs.AutoReplies}
                            className={styles.tab}
                        >
                            {t('AppConfiguration_AutoReplies_Section')}
                        </Tab>
                        <Tab
                            value={AppConfigTabs.FlightNumbers}
                            className={styles.tab}
                        >
                            {t('AppConfiguration_FlightNumbers_Section') ||
                                'Flight Numbers'}
                        </Tab>
                        <Tab
                            value={AppConfigTabs.UnableToNotifyReasons}
                            className={styles.tab}
                        >
                            {t(
                                'AppConfiguration_UnableToNotifyReasons_Section'
                            )}
                        </Tab>
                        <Tab
                            value={AppConfigTabs.AllowedCrewMembers}
                            className={styles.tab}
                        >
                            {t(
                                'AppConfiguration_AllowedCrewMembers_Section'
                            )}
                        </Tab>
                    </TabList>
                </ToolbarGroup>
                <ToolbarGroup
                    role="presentation"
                    className={mergeClasses(
                        toolbarStyles.toolbarGroup,
                        toolbarStyles.toolbarRightGroup,
                        styles.searchGroup
                    )}
                >
                    <Input
                        className={toolbarStyles.searchBar}
                        placeholder={t('SearchFlightsInputPlaceholder')!}
                        size="small"
                        contentAfter={
                            <Button
                                appearance="transparent"
                                onClick={onClearSearchTextClick}
                                icon={
                                    !search ? (
                                        <Search24Regular />
                                    ) : (
                                        <Dismiss24Regular />
                                    )
                                }
                            />
                        }
                        onChange={onQueryChange}
                        value={search}
                    />
                </ToolbarGroup>
            </Toolbar>
            {selectedValue === AppConfigTabs.WelcomeMessages && (
                <WelcomeMessagesPanel />
            )}
            {selectedValue === AppConfigTabs.CategoriesConfig && (
                <CategoriesConfigPanel />
            )}
            {selectedValue === AppConfigTabs.AutoReplies && (
                <AutoRepliesPanel />
            )}
            {selectedValue === AppConfigTabs.FlightNumbers && (
                <FlightNumbersPanel />
            )}
            {selectedValue === AppConfigTabs.UnableToNotifyReasons && (
                <UnableToNotifyReasonsPanel />
            )}
            {selectedValue === AppConfigTabs.AllowedCrewMembers && (
                <AllowedCrewMembersPanel />
            )}
            {selectedValue === AppConfigTabs.ConversationStatuses && (
                <ConversationStatusesPanel />
            )}
        </div>
    );
};

export default AppConfigurationPage;
